import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  DocumentTextIcon,
  TagIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { renderHighlightedText } from '../../utils/highlightUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

interface Creator {
  id: string;
  username: string;
  displayName?: string;
  bio?: string;
  avatar?: string;
  category?: string;
  patronCount?: number;
  postCount?: number;
  highlight?: Record<string, string[]>;
}

interface PostHit {
  id: string;
  title: string;
  mediaType?: string;
  likeCount?: number;
  viewCount?: number;
  creator?: { id: string; username?: string; displayName?: string };
  highlight?: Record<string, string[]>;
}

interface SearchFilters {
  type: 'creators' | 'posts' | 'all';
  category?: string;
  sortBy: 'popular' | 'newest' | 'az';
  minPatrons?: number;
  verified?: boolean;
  // Post filters
  mediaTypes?: string[];
  dateFrom?: string;
  dateTo?: string;
  minLikes?: number;
  minViews?: number;
  tags?: string[];
}

export default function AdvancedSearchPage() {
  const [query, setQuery] = useState('');
  const [creatorResults, setCreatorResults] = useState<Creator[]>([]);
  const [postResults, setPostResults] = useState<PostHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 12;

  const [filters, setFilters] = useState<SearchFilters>({
    type: 'creators',
    sortBy: 'popular',
  });
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const categories = [
    'Art',
    'Music',
    'Writing',
    'Video',
    'Podcasts',
    'Gaming',
    'Photography',
    'Education',
    'Technology',
    'Fitness',
    'Cooking',
    'Other',
  ];

  useEffect(() => {
    if (query.trim()) {
      performSearch();
    } else {
      setCreatorResults([]);
      setPostResults([]);
      setTotalResults(0);
    }
  }, [query, filters, currentPage]);

  const suggestionType = useMemo(() => (filters.type === 'all' ? 'all' : filters.type), [filters.type]);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const id = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/search/suggestions`, { params: { q, type: suggestionType } });
        setSuggestions(res.data?.suggestions || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [query, suggestionType]);

  const performSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      const types = filters.type === 'all' ? ['posts', 'creators'] : [filters.type];

      const postFilters: any = {
        mediaTypes: filters.mediaTypes,
        minLikes: filters.minLikes,
        minViews: filters.minViews,
        categories: filters.category ? [filters.category] : undefined,
        tags: filters.tags,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      };
      const creatorFilters: any = {
        categories: filters.category ? [filters.category] : undefined,
        isVerified: filters.verified,
        minPatrons: filters.minPatrons,
        sortBy: filters.sortBy === 'popular' ? 'popular' : filters.sortBy === 'newest' ? 'newest' : 'az',
      };

      const response = await axios.post(`${API_BASE_URL}/api/search/unified`, {
        query: query.trim(),
        types,
        postFilters,
        creatorFilters,
        limit: resultsPerPage,
        page: currentPage,
      });

      const data = response.data || {};
      const creators = data.creators?.hits || [];
      const posts = data.posts?.hits || [];
      setCreatorResults(creators);
      setPostResults(posts);
      const total = (data.creators?.total || 0) + (data.posts?.total || 0);
      setTotalResults(total);
    } catch (error) {
      console.error('Search error:', error);
      setCreatorResults([]);
      setPostResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    performSearch();
  };

  const clearFilters = () => {
    setFilters({
      type: 'creators',
      sortBy: 'popular',
      mediaTypes: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      minLikes: undefined,
      minViews: undefined,
      tags: undefined,
      minPatrons: undefined,
      verified: undefined,
      category: undefined,
    });
  };

  const handleExport = async (format: 'csv' | 'json') => {
    if (!query.trim()) return;
    
    try {
      const params: any = { q: query, format, size: 500 };
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.category) params.category = filters.category;
      if (filters.minPatrons) params.minPatrons = filters.minPatrons;
      if (filters.verified) params.verified = filters.verified;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      
      // Post filters
      if (filters.mediaTypes?.length) params.mediaTypes = filters.mediaTypes.join(',');
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.minLikes) params.minLikes = filters.minLikes;
      if (filters.minViews) params.minViews = filters.minViews;
      if (filters.tags?.length) params.tags = filters.tags.join(',');

      const response = await axios.get(`${API_BASE_URL}/api/search/export`, {
        params,
        responseType: format === 'csv' ? 'blob' : 'json',
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `search-results-${Date.now()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `search-results-${Date.now()}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export results');
    }
  };

  const hasActiveFilters = () => {
    return (
      filters.category ||
      filters.minPatrons ||
      filters.verified ||
      filters.sortBy !== 'popular'
    );
  };

  const totalPages = Math.ceil(totalResults / resultsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <MagnifyingGlassIcon className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-5xl font-bold mb-4">Advanced Search</h1>
            <p className="text-xl opacity-90">
              Find creators, content, and communities that match your interests
            </p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-4xl mx-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-6 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for creators, categories, or keywords..."
                className="w-full pl-16 pr-32 py-5 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-white shadow-lg"
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </button>
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
                  {suggestions.map((s: any, idx: number) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        if (s.type === 'creator') setQuery(s.name || s.username || '');
                        if (s.type === 'post') setQuery(s.title || '');
                        setShowSuggestions(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span className="truncate">
                        {s.type === 'creator' ? (
                          <>
                            <span className="text-purple-700 font-medium mr-2">Creator</span>
                            <span className="text-gray-900">{s.name || s.username}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-pink-700 font-medium mr-2">Post</span>
                            <span className="text-gray-900">{s.title}</span>
                          </>
                        )}
                      </span>
                      <span className="text-xs text-gray-400 ml-4">{s.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </form>

          {/* Filter Pills */}
          {hasActiveFilters() && (
            <div className="max-w-4xl mx-auto mt-4 flex flex-wrap gap-2">
              {filters.category && (
                <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm flex items-center">
                  Category: {filters.category}
                  <button
                    onClick={() => setFilters({ ...filters, category: undefined })}
                    className="ml-2 hover:bg-white/20 rounded-full p-1"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              )}
              {filters.sortBy !== 'popular' && (
                <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm flex items-center">
                  Sort: {filters.sortBy === 'newest' ? 'Newest' : 'A-Z'}
                  <button
                    onClick={() => setFilters({ ...filters, sortBy: 'popular' })}
                    className="ml-2 hover:bg-white/20 rounded-full p-1"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              )}
              {filters.minPatrons && (
                <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm flex items-center">
                  Min Patrons: {filters.minPatrons}
                  <button
                    onClick={() => setFilters({ ...filters, minPatrons: undefined })}
                    className="ml-2 hover:bg-white/20 rounded-full p-1"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm hover:bg-white/30"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filters */}
          {showFilters && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2" />
                    Filters
                  </h3>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="lg:hidden text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Search Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Type
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) =>
                      setFilters({ ...filters, type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                  >
                    <option value="creators">Creators</option>
                    <option value="posts">Posts</option>
                    <option value="all">All</option>
                  </select>
                </div>

                {/* Category */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={filters.category || ''}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        category: e.target.value || undefined,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort By */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) =>
                      setFilters({ ...filters, sortBy: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                  >
                    <option value="popular">Most Popular</option>
                    <option value="newest">Newest</option>
                    <option value="az">A-Z</option>
                  </select>
                </div>

                {/* Min Patrons */}
                {filters.type === 'creators' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Patrons
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={filters.minPatrons || ''}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          minPatrons: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="Any"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                    />
                  </div>
                )}

                {/* Verified Only */}
                {filters.type === 'creators' && (
                  <div className="mb-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.verified || false}
                        onChange={(e) =>
                          setFilters({ ...filters, verified: e.target.checked })
                        }
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Verified creators only
                      </span>
                    </label>
                  </div>
                )}

                {/* Post Filters */}
                {(filters.type === 'posts' || filters.type === 'all') && (
                  <>
                    {/* Media Types */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Media Types
                      </label>
                      <div className="space-y-2">
                        {['image', 'video', 'audio', 'text'].map((type) => (
                          <label key={type} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={filters.mediaTypes?.includes(type) || false}
                              onChange={(e) => {
                                const current = filters.mediaTypes || [];
                                const updated = e.target.checked
                                  ? [...current, type]
                                  : current.filter((t) => t !== type);
                                setFilters({
                                  ...filters,
                                  mediaTypes: updated.length > 0 ? updated : undefined,
                                });
                              }}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-600"
                            />
                            <span className="ml-2 text-sm text-gray-700 capitalize">
                              {type}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date Range
                      </label>
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={filters.dateFrom || ''}
                          onChange={(e) =>
                            setFilters({ ...filters, dateFrom: e.target.value || undefined })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                          placeholder="From"
                        />
                        <input
                          type="date"
                          value={filters.dateTo || ''}
                          onChange={(e) =>
                            setFilters({ ...filters, dateTo: e.target.value || undefined })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                          placeholder="To"
                        />
                      </div>
                    </div>

                    {/* Min Likes */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Likes
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={filters.minLikes || ''}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            minLikes: e.target.value ? parseInt(e.target.value) : undefined,
                          })
                        }
                        placeholder="Any"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                      />
                    </div>

                    {/* Min Views */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Views
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={filters.minViews || ''}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            minViews: e.target.value ? parseInt(e.target.value) : undefined,
                          })
                        }
                        placeholder="Any"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                      />
                    </div>

                    {/* Tags */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tags
                      </label>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && tagInput.trim()) {
                                e.preventDefault();
                                const current = filters.tags || [];
                                if (!current.includes(tagInput.trim())) {
                                  setFilters({
                                    ...filters,
                                    tags: [...current, tagInput.trim()],
                                  });
                                }
                                setTagInput('');
                              }
                            }}
                            placeholder="Add tag and press Enter"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
                          />
                        </div>
                        {filters.tags && filters.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {filters.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                              >
                                {tag}
                                <button
                                  onClick={() => {
                                    const updated = filters.tags?.filter((_, i) => i !== idx);
                                    setFilters({
                                      ...filters,
                                      tags: updated?.length ? updated : undefined,
                                    });
                                  }}
                                  className="ml-2 hover:text-purple-900"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Apply Filters Button */}
                <button
                  onClick={performSearch}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          <div className={showFilters ? 'lg:col-span-3' : 'lg:col-span-4'}>
            {/* Results Header */}
            {query && (
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {loading ? 'Searching...' : `${totalResults} Results`}
                  </h2>
                  <p className="text-gray-600 mt-1">
                    for "{query}" in {filters.type === 'creators' ? 'creators' : filters.type}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {!loading && totalResults > 0 && (
                    <>
                      <button
                        onClick={() => handleExport('csv')}
                        className="flex items-center text-sm bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <DocumentTextIcon className="h-4 w-4 mr-2" />
                        Export CSV
                      </button>
                      <button
                        onClick={() => handleExport('json')}
                        className="flex items-center text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <DocumentTextIcon className="h-4 w-4 mr-2" />
                        Export JSON
                      </button>
                    </>
                  )}
                  {!showFilters && (
                    <button
                      onClick={() => setShowFilters(true)}
                      className="flex items-center text-purple-600 hover:text-purple-700 font-medium"
                    >
                      <FunnelIcon className="h-5 w-5 mr-2" />
                      Show Filters
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <p className="mt-4 text-gray-600">Searching...</p>
              </div>
            )}

            {/* No Query State */}
            {!query && !loading && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <MagnifyingGlassIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Start Searching
                </h3>
                <p className="text-gray-600">
                  Enter a search term above to discover creators and content
                </p>
              </div>
            )}

            {/* No Results State */}
            {query && !loading && creatorResults.length + postResults.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-600 text-lg mb-4">
                  No results found for "{query}"
                </p>
                <p className="text-gray-500 mb-6">
                  Try different keywords or adjust your filters
                </p>
                <button
                  onClick={clearFilters}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {/* Results Grid */}
            {!loading && (creatorResults.length > 0 || postResults.length > 0) && (
              <>
                {creatorResults.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {creatorResults.map((creator) => (
                    <Link
                      key={creator.id}
                      to={`/${creator.username}`}
                      className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                    >
                      {/* Avatar */}
                      <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-8">
                        <img
                          src={creator.avatar || '/default-avatar.png'}
                          alt={creator.displayName || creator.username}
                          className="h-32 w-32 rounded-full mx-auto object-cover border-4 border-white shadow-lg"
                        />
                      </div>

                      <div className="p-6">
                        {/* Name & Username */}
                        <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                          {creator.displayName || creator.username}
                        </h3>
                        <p className="text-center text-gray-600 text-sm mb-3">
                          @{creator.username}
                        </p>

                        {/* Category */}
                        {creator.category && (
                          <div className="flex items-center justify-center mb-3">
                            <TagIcon className="h-4 w-4 text-purple-600 mr-1" />
                            <span className="text-sm text-gray-600">{creator.category}</span>
                          </div>
                        )}

                        {/* Bio */}
                        {creator.bio && (
                          <p className="text-gray-600 text-sm text-center mb-4 line-clamp-2">
                            {renderHighlightedText(creator.bio, creator.highlight, 'bio', 100)}
                          </p>
                        )}

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                          <div className="text-center">
                            <UserGroupIcon className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                            <div className="text-lg font-bold text-gray-900">
                              {creator.patronCount || 0}
                            </div>
                            <div className="text-xs text-gray-600">Patrons</div>
                          </div>
                          <div className="text-center">
                            <DocumentTextIcon className="h-5 w-5 text-pink-600 mx-auto mb-1" />
                            <div className="text-lg font-bold text-gray-900">
                              {creator.postCount || 0}
                            </div>
                            <div className="text-xs text-gray-600">Posts</div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                )}

                {postResults.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {postResults.map((post) => (
                      <div key={post.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                          {renderHighlightedText(post.title || 'Untitled Post', post.highlight, 'title', 80)}
                        </h4>
                        <p className="text-sm text-gray-500 mb-2">{post.creator?.displayName || post.creator?.username || 'Creator'}</p>
                        <div className="flex items-center justify-between text-sm text-gray-600">
                          <span>{post.mediaType || 'TEXT'}</span>
                          <span>👍 {post.likeCount || 0} • 👁️ {post.viewCount || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-4 py-2 rounded-lg ${
                            currentPage === page
                              ? 'bg-purple-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
